import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import { Environment } from '~/engine/data/Environment';

interface UsePreviewBaseOptions {
    environment: Environment | null;
}

export interface PreviewBaseResult {
    isPreviewing: boolean;
    setIsPreviewing: Dispatch<SetStateAction<boolean>>;
    updateView: () => void;
    /** Start a new abortable operation. Returns the signal to check inside async work. */
    beginOperation: () => AbortSignal;
    /**
     * Publish a wrapper to the preview slot, disposing any previously-held
     * wrapper. `key` is optional — supply it only when the caller intends to
     * later hand off the texture via `consumeResult`.
     */
    setResult: (previewObj: CustomTargetObject, key?: string) => void;
    /** Hand off the held result's texture if its key matches the requested key. */
    consumeResult: (key: string) => ContourTexture | undefined;
    /** Abort any in-flight compute without touching the held result. */
    abortInFlight: () => void;
    /** Abort any in-flight compute and dispose the held result. */
    cleanup: () => void;
}

/** Result-lifecycle infrastructure shared by SDF-operation preview hooks. */
export function usePreviewBase({
    environment,
}: UsePreviewBaseOptions): PreviewBaseResult {
    const [isPreviewing, setIsPreviewing] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const resultRef = useRef<{
        key?: string;
        previewObj: CustomTargetObject;
    } | null>(null);

    const updateView = useCallback(() => {
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                environment?.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }, [environment?.structureSet]);

    const invalidateResult = useCallback(() => {
        const result = resultRef.current;
        if (!result) {
            return;
        }
        if (environment?.operationPreviewStructure === result.previewObj) {
            environment.operationPreviewStructure = undefined;
        }
        result.previewObj.delete();
        resultRef.current = null;
        updateView();
    }, [environment, updateView]);

    const setResult = useCallback(
        (previewObj: CustomTargetObject, key?: string) => {
            if (!environment) {
                invalidateResult();
                previewObj.delete();
                return;
            }
            // Swap atomically: publish the new preview, THEN dispose the old
            // wrapper, with a single updateView(). operationPreviewStructure is
            // never undefined mid-swap, so the other structures never un-dim
            // for a frame.
            const previous = resultRef.current;
            resultRef.current = { key, previewObj };
            environment.operationPreviewStructure = previewObj;
            if (previous && previous.previewObj !== previewObj) {
                previous.previewObj.delete();
            }
            updateView();
        },
        [environment, invalidateResult, updateView]
    );

    const consumeResult = useCallback(
        (key: string): ContourTexture | undefined => {
            const result = resultRef.current;
            if (!result || result.key !== key) {
                return undefined;
            }
            const texture = result.previewObj.contourTexture;
            if (environment?.operationPreviewStructure === result.previewObj) {
                environment.operationPreviewStructure = undefined;
            }
            resultRef.current = null;
            updateView();
            return texture;
        },
        [environment, updateView]
    );

    const abortInFlight = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    const cleanup = useCallback(() => {
        abortInFlight();
        invalidateResult();
    }, [abortInFlight, invalidateResult]);

    const beginOperation = useCallback((): AbortSignal => {
        // Abort any in-flight compute, but KEEP the currently-displayed
        // preview on screen until its replacement is ready. `setResult`
        // disposes the old wrapper and publishes the new one atomically
        // (single `updateView`), so there is no frame without a preview.
        // Calling `cleanup()` here instead would clear
        // `operationPreviewStructure` for the whole async recompute window,
        // which un-dims the other structures and reads as a flicker.
        abortInFlight();
        const controller = new AbortController();
        abortRef.current = controller;
        return controller.signal;
    }, [abortInFlight]);

    // Dispose any still-held result when the consumer unmounts.
    const cleanupRef = useRef(cleanup);
    cleanupRef.current = cleanup;
    useEffect(() => () => cleanupRef.current(), []);

    return useMemo(
        () => ({
            isPreviewing,
            setIsPreviewing,
            updateView,
            beginOperation,
            setResult,
            consumeResult,
            abortInFlight,
            cleanup,
        }),
        [
            isPreviewing,
            setIsPreviewing,
            updateView,
            beginOperation,
            setResult,
            consumeResult,
            abortInFlight,
            cleanup,
        ]
    );
}
